import React, { useState, useEffect } from 'react';
import {
    FaTimes,
    FaBuilding,
    FaSave,
    FaTags,
    FaToggleOn,
    FaToggleOff,
    FaTrash
} from 'react-icons/fa';
import styles from './EditClientModal.module.css';
import { clientService } from '../../services/clientService';
import { clientTypeTagsService } from '../../services/clientTypeTagsService';
import { useAccessToken, useLockBodyScroll } from '../../hooks';
import { useModalAlert } from '../../hooks/useModalAlert';
import { ClientTypeTagManager } from '../clientTypeTags';
import ModalAlert from './ModalAlert';

/**
 * Edit Client Modal Component
 * Modal for editing an existing client with active/inactive status and client type management
 */
const EditClientModal = ({ 
    client, 
    isOpen, 
    onClose, 
    onClientUpdated, 
    clientTypes, 
    allClientTypeAssignments 
}) => {
    const { getBackendAccessToken } = useAccessToken();
    const { modalAlert, setInfoAlert, setErrorAlert, clearAlert } = useModalAlert();

    // Lock body scroll when modal is open
    useLockBodyScroll(isOpen);

    // Form state
    const [formData, setFormData] = useState({
        clientCode: '',
        clientName: '',
        isActive: true
    });

    // Client type assignments state
    const [pendingTagChanges, setPendingTagChanges] = useState([]);
    const [initialAssignments, setInitialAssignments] = useState([]);

    // UI state
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const [step, setStep] = useState(1); // 1: Client Info, 2: Actions
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [showUnsavedWarning, setShowUnsavedWarning] = useState(false);
    const [flashSaveButton, setFlashSaveButton] = useState(false);

    // Store original form data to detect changes
    const [originalFormData, setOriginalFormData] = useState({});

    /**
     * Check if form data has changed from original
     */
    const checkForUnsavedChanges = (currentData, originalData) => {
        // Compare all form fields
        const fieldsToCheck = ['clientCode', 'clientName', 'isActive'];
        
        return fieldsToCheck.some(field => {
            const current = currentData[field];
            const original = originalData[field];
            
            // Handle boolean comparisons
            if (typeof current === 'boolean' && typeof original === 'boolean') {
                return current !== original;
            }
            
            // Handle string comparisons (treat empty strings and undefined/null as equivalent)
            const currentValue = current || '';
            const originalValue = original || '';
            return currentValue !== originalValue;
        });
    };

    /**
     * Check if client type tags have changed
     */
    const checkForTagChanges = (currentTags, originalTags) => {
        // If lengths are different, there's a change
        if (currentTags.length !== originalTags.length) {
            return true;
        }

        // Check if all tags in current exist in original
        return currentTags.some(currentTag => 
            !originalTags.some(originalTag => 
                originalTag.clientTypeId === currentTag.clientTypeId
            )
        );
    };

    /**
     * Initialize form data when client changes
     */
    useEffect(() => {
        if (client && isOpen) {
            // Convert client data to form format
            const isActiveBoolean = client.isActive ?? true;

            const initialData = {
                clientCode: client.clientCode || '',
                clientName: client.clientName || '',
                isActive: isActiveBoolean
            };

            setFormData(initialData);
            setOriginalFormData(initialData);

            // Initialize client type assignments
            const assignments = allClientTypeAssignments[client.clientId] || [];
            const assignmentsCopy = JSON.parse(JSON.stringify(assignments)); // Deep clone
            setInitialAssignments(assignmentsCopy);
            setPendingTagChanges(assignmentsCopy);

            setErrors({});
            setHasUnsavedChanges(false);
            setShowUnsavedWarning(false);
            setFlashSaveButton(false);
            // Only reset step when modal first opens, not on subsequent updates
        }
    }, [client, isOpen, allClientTypeAssignments]);

    /**
     * Reset form when modal opens/closes
     */
    useEffect(() => {
        if (isOpen) {
            // Reset step when modal opens
            setStep(1);
        } else {
            // Reset when modal closes
            setErrors({});
            setStep(1);
            setHasUnsavedChanges(false);
            setShowUnsavedWarning(false);
            setFlashSaveButton(false);
            clearAlert();
        }
    }, [isOpen, clearAlert]);

    /**
     * Handle form field changes
     */
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        const newFormData = {
            ...formData,
            [name]: type === 'checkbox' ? checked : (name === 'clientCode' ? value.toUpperCase() : value)
        };
        
        setFormData(newFormData);

        // Check for unsaved changes (both form and tags)
        const formChanges = checkForUnsavedChanges(newFormData, originalFormData);
        const tagChanges = checkForTagChanges(pendingTagChanges, initialAssignments);
        setHasUnsavedChanges(formChanges || tagChanges);

        // Clear field error when user starts typing
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: null }));
        }
    };

    /**
     * Handle client type tags change
     */
    const handleTagsChange = (updatedAssignments) => {
        setPendingTagChanges(updatedAssignments);
        
        // Check if tags have changed and update unsaved changes flag
        const formChanges = checkForUnsavedChanges(formData, originalFormData);
        const tagChanges = checkForTagChanges(updatedAssignments, initialAssignments);
        setHasUnsavedChanges(formChanges || tagChanges);
    };

    /**
     * Validate form data
     */
    const validateForm = () => {
        const newErrors = {};
        const trimmedCode = (formData.clientCode || '').trim();
        const trimmedName = (formData.clientName || '').trim();
        
        if (!trimmedCode && !trimmedName) {
            newErrors.general = 'Either client code or client name must be provided';
        }
        if (trimmedCode && trimmedCode.length > 6) {
            newErrors.clientCode = 'Client code cannot exceed 6 characters';
        }
        if (trimmedName && trimmedName.length > 50) {
            newErrors.clientName = 'Client name cannot exceed 50 characters';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    /**
     * Handle next step (from client info to actions)
     */
    const handleNext = () => {
        // Check for unsaved changes on Step 1
        if (step === 1 && hasUnsavedChanges) {
            if (!showUnsavedWarning) {
                // First attempt - show warning and flash save button
                setShowUnsavedWarning(true);
                setErrorAlert('You have unsaved changes. Click "Save Changes" to save your work, or click "Next" again to proceed without saving.');
                
                // Flash the save button
                setFlashSaveButton(true);
                setTimeout(() => setFlashSaveButton(false), 2000);
                
                return;
            }
            // Second attempt - user confirmed, proceed without saving
            // Reset the warning flag and allow navigation
            setShowUnsavedWarning(false);
            setFlashSaveButton(false);
            clearAlert();
        }
        
        if (validateForm()) {
            // Reset warning states when moving to step 2
            setShowUnsavedWarning(false);
            setFlashSaveButton(false);
            setHasUnsavedChanges(false); // Reset since we're discarding changes
            clearAlert();
            setStep(2);
        }
    };

    /**
     * Handle previous step
     */
    const handlePrevious = () => {
        setShowUnsavedWarning(false);
        setFlashSaveButton(false);
        clearAlert();
        setStep(1);
    };

    /**
     * Save client type tags to API
     */
    const saveClientTypeTags = async (clientId, originalAssignments, newAssignments) => {
        const accessToken = await getBackendAccessToken();

        // Find assignments to add (in new but not in original)
        const toAdd = newAssignments.filter(newAssignment => 
            !originalAssignments.some(orig => orig.clientTypeId === newAssignment.clientTypeId)
        );

        // Find assignments to remove (in original but not in new)
        const toRemove = originalAssignments.filter(origAssignment => 
            !newAssignments.some(newAssignment => newAssignment.clientTypeId === origAssignment.clientTypeId)
        );

        // Process additions
        for (const assignment of toAdd) {
            await clientTypeTagsService.addClientTypeToClient(accessToken, clientId, assignment.clientTypeId);
        }

        // Process removals
        for (const assignment of toRemove) {
            await clientTypeTagsService.removeClientTypeFromClient(accessToken, clientId, assignment.clientTypeId);
        }
    };

    /**
     * Handle form submission for updating client
     */
    const handleSubmit = async (e) => {
        e.preventDefault();

        // Validate one more time
        if (!validateForm()) {
            setStep(1); // Go back to step 1 if validation fails
            return;
        }

        setSaving(true);

        try {
            const accessToken = await getBackendAccessToken();

            // Prepare submission data with trimmed values
            const submissionData = {
                clientCode: (formData.clientCode || '').trim(),
                clientName: (formData.clientName || '').trim(),
                isActive: formData.isActive
            };

            // Update the client
            const updatedClient = await clientService.update(accessToken, client.clientId, submissionData);

            // Save client type tag changes
            await saveClientTypeTags(client.clientId, initialAssignments, pendingTagChanges);

            // Success!
            setInfoAlert(
                `Client "${updatedClient.clientName || updatedClient.clientCode}" updated successfully!`
            );

            // Update original form data to reflect saved state
            setOriginalFormData(formData);
            setHasUnsavedChanges(false);

            // Call callback with updated client (this will close the modal)
            if (onClientUpdated) {
                onClientUpdated(updatedClient, false, true); // false = not deleted, true = close modal
            }
        } catch (error) {
            setErrorAlert(`Failed to update client: ${error.message || 'Unknown error'}`);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Handle toggle client active status
     */
    const handleToggleStatus = async () => {
        if (!client) return;

        const clientName = client.clientName || client.clientCode;
        const action = formData.isActive ? 'deactivate' : 'activate';
        const actionPast = formData.isActive ? 'deactivated' : 'activated';
        
        // Show confirmation dialog
        const confirmAction = window.confirm(
            `Are you sure you want to ${action} "${clientName}"?\n\n${
                formData.isActive 
                    ? 'This will hide the client from most views in the system.'
                    : 'This will make the client visible in the system again.'
            }`
        );
        
        if (!confirmAction) {
            return;
        }

        setSaving(true);

        try {
            const accessToken = await getBackendAccessToken();
            
            let updatedClient;
            if (formData.isActive) {
                // Currently active, so deactivate
                updatedClient = await clientService.deactivate(accessToken, client.clientId);
            } else {
                // Currently inactive, so activate
                updatedClient = await clientService.activate(accessToken, client.clientId);
            }
            
            setInfoAlert(`Client "${clientName}" has been ${actionPast} successfully`);
            
            // Call callback with updated client
            if (onClientUpdated) {
                onClientUpdated(updatedClient, false, false); // false = not deleted, false = don't close modal
            }
            
            // Close modal after successful action
            handleClose();
        } catch (error) {
            console.error(`Failed to ${action} client:`, error);
            setErrorAlert(`Failed to ${action} client: ${error.message || 'Unknown error'}`);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Handle delete client (if needed in the future)
     * Currently disabled per business requirements
     */
    const handleDelete = async () => {
        if (!client) return;

        const clientName = client.clientName || client.clientCode || 'this client';
        
        // Show confirmation dialog
        const confirmDelete = window.confirm(
            `Are you sure you want to delete "${clientName}"?\n\nThis action cannot be undone and will permanently remove the client from the system.`
        );
        
        if (!confirmDelete) {
            return;
        }
        
        setSaving(true);

        try {
            const accessToken = await getBackendAccessToken();
            
            await clientService.delete(accessToken, client.clientId);
            
            setInfoAlert(`Client "${clientName}" has been successfully deleted`);
            
            // Call callback to refresh the list (this will close the modal)
            if (onClientUpdated) {
                onClientUpdated(null, true, true); // null = no updated client, true = deleted, true = close modal
            }
        } catch (error) {
            console.error('Failed to delete client:', error);
            
            // Extract the specific error message from the backend response
            let errorMessage = `Failed to delete client "${clientName}".`;
            
            if (error.response?.data?.reason) {
                errorMessage = error.response.data.reason;
            } else if (error.message && error.message !== 'Failed to delete client') {
                errorMessage = error.message;
            } else {
                errorMessage += ' Please try again.';
            }
            
            setErrorAlert(errorMessage);
        } finally {
            setSaving(false);
        }
    };

    /**
     * Handle modal close
     */
    const handleClose = () => {
        // Check for unsaved changes only on Step 1 (where editing happens)
        if (step === 1 && hasUnsavedChanges && !showUnsavedWarning) {
            // First close attempt - show warning and flash save button
            setShowUnsavedWarning(true);
            setErrorAlert('You have unsaved changes. Click "Save Changes" to save your work, or close again to discard changes.');
            
            // Flash the save button
            setFlashSaveButton(true);
            setTimeout(() => setFlashSaveButton(false), 2000);
            
            return;
        }
        
        // Reset all states and close
        setErrors({});
        setStep(1);
        setHasUnsavedChanges(false);
        setShowUnsavedWarning(false);
        setFlashSaveButton(false);
        clearAlert();
        onClose();
    };

    // Don't render if not open or no client
    if (!isOpen || !client) return null;

    return (
        <div className={styles.modalOverlay}>
            <div className={styles.modalContent}>
                {/* Modal Header */}
                <div className={styles.modalHeader}>
                    <div className={styles.headerInfo}>
                        <div className={styles.headerIcon}>
                            <FaBuilding />
                        </div>
                        <div>
                            <h2 className={styles.modalTitle}>
                                Edit Client
                            </h2>
                            <p className={styles.modalSubtitle}>
                                {client.clientName || client.clientCode || 'Client'}
                            </p>
                        </div>
                    </div>
                    <button
                        className={styles.closeButton}
                        onClick={handleClose}
                        title="Close"
                        disabled={saving}
                    >
                        <FaTimes />
                    </button>
                </div>

                {/* Modal Alert - Shows inside the modal */}
                <ModalAlert
                    alert={modalAlert}
                    onClose={clearAlert}
                    autoHideDuration={5000}
                />

                {/* Step 1 - Client Information */}
                {step === 1 && (
                    <form onSubmit={handleSubmit} className={styles.form}>
                        {/* Step Indicator */}
                        <div className={styles.stepIndicator}>
                            <div className={`${styles.step} ${styles.active}`}>
                                <div className={styles.stepNumber}>1</div>
                                <div className={styles.stepLabel}>Edit Info</div>
                            </div>
                            <div className={styles.stepLine}></div>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>2</div>
                                <div className={styles.stepLabel}>Actions</div>
                            </div>
                        </div>

                        <div className={styles.formStep}>
                            <h3 className={styles.sectionTitle}>
                                <FaBuilding /> Client Information
                            </h3>

                            {errors.general && (
                                <div className={styles.errorMessage}>{errors.general}</div>
                            )}

                            {/* Client Code */}
                            <div className={styles.formGroup}>
                                <label htmlFor="clientCode" className={styles.label}>
                                    Client Code (max 6 characters)
                                </label>
                                <input
                                    type="text"
                                    id="clientCode"
                                    name="clientCode"
                                    className={`${styles.input} ${errors.clientCode ? styles.inputError : ''}`}
                                    value={formData.clientCode}
                                    onChange={handleChange}
                                    maxLength={6}
                                    disabled={saving}
                                    placeholder="ABC123"
                                />
                                {errors.clientCode && (
                                    <span className={styles.errorMessage}>{errors.clientCode}</span>
                                )}
                            </div>

                            {/* Client Name */}
                            <div className={styles.formGroup}>
                                <label htmlFor="clientName" className={styles.label}>
                                    Client Name (max 50 characters)
                                </label>
                                <input
                                    type="text"
                                    id="clientName"
                                    name="clientName"
                                    className={`${styles.input} ${errors.clientName ? styles.inputError : ''}`}
                                    value={formData.clientName}
                                    onChange={handleChange}
                                    maxLength={50}
                                    disabled={saving}
                                    placeholder="Client Company Name"
                                />
                                {errors.clientName && (
                                    <span className={styles.errorMessage}>{errors.clientName}</span>
                                )}
                            </div>

                            {/* Client Type Tags Section */}
                            <h4 className={styles.subsectionTitle}>
                                <FaTags /> Client Tags
                            </h4>
                            
                            <p className={styles.helpText}>
                                Assign client types to categorize this client. Changes will be saved when you update the client.
                            </p>

                            <div className={styles.formGroup}>
                                <div className={styles.formTagsContainer}>
                                    <ClientTypeTagManager
                                        clientId={client.clientId}
                                        clientName={client.clientName}
                                        availableClientTypes={clientTypes}
                                        preloadedAssignments={pendingTagChanges}
                                        size="medium"
                                        maxVisible={10}
                                        compact={false}
                                        readOnly={false}
                                        localOnly={true}
                                        onUpdate={handleTagsChange}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleClose}
                                disabled={saving}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                className={`${styles.submitButton} ${flashSaveButton ? styles.flashButton : ''}`}
                                disabled={saving}
                            >
                                {saving ? (
                                    <>
                                        <span className={styles.spinner}></span>
                                        Saving Changes...
                                    </>
                                ) : (
                                    <>
                                        <FaSave /> Save Changes
                                    </>
                                )}
                            </button>
                            <button
                                type="button"
                                className={styles.nextButton}
                                onClick={handleNext}
                                disabled={saving}
                            >
                                Next: Actions →
                            </button>
                        </div>
                    </form>
                )}

                {/* Step 2 - Actions */}
                {step === 2 && (
                    <div className={styles.form}>
                        {/* Step Indicator */}
                        <div className={styles.stepIndicator}>
                            <div className={styles.step}>
                                <div className={styles.stepNumber}>1</div>
                                <div className={styles.stepLabel}>Edit Info</div>
                            </div>
                            <div className={styles.stepLine}></div>
                            <div className={`${styles.step} ${styles.active}`}>
                                <div className={styles.stepNumber}>2</div>
                                <div className={styles.stepLabel}>Actions</div>
                            </div>
                        </div>

                        <div className={styles.formStep}>
                            <h3 className={styles.sectionTitle}>
                                <FaTags /> Actions
                            </h3>

                            <p className={styles.helpText}>
                                These actions will be executed immediately after confirmation. No additional saving is required.
                            </p>

                            {/* Actions Grid */}
                            <div className={styles.actionsGrid}>
                                {/* Toggle Active Status */}
                                <div className={styles.actionCard}>
                                    <div className={styles.actionIcon}>
                                        {formData.isActive ? <FaToggleOn /> : <FaToggleOff />}
                                    </div>
                                    <div className={styles.actionInfo}>
                                        <h4 className={styles.actionTitle}>
                                            {formData.isActive ? 'Active Client' : 'Inactive Client'}
                                        </h4>
                                        <p className={styles.actionDescription}>
                                            {formData.isActive 
                                                ? 'Deactivate this client to hide it from most views. You will be asked to confirm this action.'
                                                : 'Activate this client to make it visible in the system. You will be asked to confirm this action.'
                                            }
                                        </p>
                                        <button
                                            type="button"
                                            className={`${styles.actionButton} ${formData.isActive ? styles.warning : styles.success}`}
                                            onClick={handleToggleStatus}
                                            disabled={saving}
                                        >
                                            {saving ? 'Processing...' : (formData.isActive ? 'Deactivate Client' : 'Activate Client')}
                                        </button>
                                    </div>
                                </div>

                                {/* Delete Client - Currently disabled per business requirements */}
                                {/* Uncomment this section if delete functionality is needed in the future */}
                                {/*
                                <div className={styles.actionCard}>
                                    <div className={`${styles.actionIcon} ${styles.danger}`}>
                                        <FaTrash />
                                    </div>
                                    <div className={styles.actionInfo}>
                                        <h4 className={styles.actionTitle}>Delete Client</h4>
                                        <p className={styles.actionDescription}>
                                            Permanently remove this client from the system. This action cannot be undone.
                                        </p>
                                        <button
                                            type="button"
                                            className={`${styles.actionButton} ${styles.danger}`}
                                            onClick={handleDelete}
                                            disabled={saving}
                                        >
                                            Delete Client
                                        </button>
                                    </div>
                                </div>
                                */}
                            </div>
                        </div>

                        {/* Form Actions */}
                        <div className={styles.formActions}>
                            <button
                                type="button"
                                className={styles.backButton}
                                onClick={handlePrevious}
                                disabled={saving}
                            >
                                ← Back to Client Info
                            </button>
                            <button
                                type="button"
                                className={styles.cancelButton}
                                onClick={handleClose}
                                disabled={saving}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EditClientModal;